/**
 * TabSurfaces — every tab in a pane group, mounted at once.
 *
 * This is the flip the whole live-tabs design was built toward. A tab was a
 * bookmark: switching to one navigated the router, which destroyed the page you
 * left and rebuilt the one you arrived at, so every tab came back as a fresh
 * page load. A tab is now a live surface — it stays mounted, and switching
 * shows and hides.
 *
 * `<Activity mode="hidden">` is what makes that affordable. It keeps the
 * subtree's state and its DOM (so scroll position, drafts and expanded rows
 * survive untouched) while tearing its effects down, so a hidden tab's polls,
 * sockets, observers and key listeners all stop by themselves.
 *
 * Three things here are load-bearing:
 *
 * - **`key={tab.id}`.** Tab identity, not position or path, is what keeps an
 *   instance stable across reordering and navigation.
 * - **One provider position for every tab.** A tab that changed which provider
 *   wrapped it would change shape in the element tree and remount, losing
 *   exactly what this exists to preserve. `TabRouterContext` always sits here
 *   and decides internally.
 * - **The active primary tab renders the real URL; every other tab renders its
 *   stored path.** The primary group owns `window.location` and the tab's path
 *   is the mirror of it. Rendering the mirror would show a frame of the
 *   previously-stored path whenever the two disagree — a deep link at startup,
 *   most visibly.
 *
 * Moving a tab between panes changes its parent and therefore remounts it. That
 * is accepted rather than fixed: rendering every tab from one list positioned
 * by CSS would couple the tab model to the pane layout for something that
 * happens rarely. See the design's architecture section.
 */
import React, { Activity, Suspense, useMemo } from 'react';

import { Navigate, useLocation } from 'react-router-dom';

import LoadingSpinner from '@shared/components/feedback/LoadingSpinner';
import { RouteSurface } from '@shared/components/layout';
import { ATTENTION_ROUTES } from '@shared/constants/routes';
import { SurfaceProvider } from '@shared/surface';

import { TabRouterContext } from './TabRouterContext';
import { splitFullPath } from '../helpers/splitFullPath';
import { resolveRoute } from '../resolveRoute';
import { tabsForGroup, useWorkspaceStore } from '../stores/useWorkspaceStore';

import type { PaneGroup, WorkspaceTab } from '../types';

/** A tab whose stored path matches no route — a page removed since it was opened. */
const NotAvailable: React.FC<{ path: string }> = ({ path }) => (
  <div className="text-muted-foreground flex h-full items-center justify-center p-8 text-center">
    <div>
      <p className="text-sm font-medium">Page not available</p>
      <p className="mt-1 text-xs">{path}</p>
    </div>
  </div>
);

/** One tab's page, with the router context and scroll container it needs. */
const TabSurface: React.FC<{
  tab: WorkspaceTab;
  /** What this tab renders: the live URL for the primary active tab, else `tab.path`. */
  path: string;
  ownsBrowserUrl: boolean;
}> = ({ tab, path, ownsBrowserUrl }) => {
  const resolved = resolveRoute(splitFullPath(path).pathname);
  if (!resolved) return <NotAvailable path={path} />;

  return (
    <SurfaceProvider surfaceId={tab.id}>
      <TabRouterContext
        tab={tab}
        path={path}
        params={resolved.params}
        ownsBrowserUrl={ownsBrowserUrl}
      >
        <RouteSurface layout={resolved.route.layout}>
          {/* Pages are code-split (see `@app/routes/lazyPages`), so the element
              can suspend while its chunk downloads. The boundary sits inside
              the tab so the shell — nav rail, side pane, chat dock — stays
              mounted rather than blanking on every first visit to a feature. */}
          <Suspense fallback={<LoadingSpinner className="h-full" message="" />}>
            {resolved.route.element}
          </Suspense>
        </RouteSurface>
      </TabRouterContext>
    </SurfaceProvider>
  );
};

/**
 * The group's tabs, in strip order, with the active one visible.
 *
 * The primary group's active tab is synthesised from the URL when the store has
 * no tab for it. The URL is authoritative either way, so a page renders rather
 * than waiting for hydration to agree with it.
 */
function useGroupTabs(group: PaneGroup, activeTabId: string | null, urlPath: string) {
  const tabs = useWorkspaceStore(s => s.tabs);
  return useMemo(() => {
    const groupTabs = tabsForGroup(tabs, group);
    if (group !== 'primary' || groupTabs.some(t => t.id === activeTabId)) return groupTabs;
    return [
      ...groupTabs,
      { id: activeTabId ?? 'primary', title: '', path: urlPath, pinned: false, group },
    ];
  }, [tabs, group, activeTabId, urlPath]);
}

export const TabSurfaces: React.FC<{ group: PaneGroup }> = ({ group }) => {
  const location = useLocation();
  const activeTabId = useWorkspaceStore(s =>
    group === 'primary' ? s.primaryActiveTabId : s.secondaryActiveTabId
  );
  const urlPath = `${location.pathname}${location.search}${location.hash}`;
  const groupTabs = useGroupTabs(group, activeTabId, urlPath);
  const urlResolves = resolveRoute(location.pathname) !== null;

  // Nothing matched the URL. Without this the app renders a blank white screen,
  // which reads as "the app is broken" rather than "that URL is gone". This was
  // the router's `path="*"` catch-all; the table resolves the URL now, so the
  // fallback belongs where the resolution happens. Only the group that owns the
  // URL answers for it — a side-pane tab shows NotAvailable instead.
  if (group === 'primary' && !urlResolves) {
    return <Navigate to={ATTENTION_ROUTES.root} replace />;
  }

  return (
    <>
      {groupTabs.map(tab => {
        const isActive = tab.id === activeTabId;
        const ownsBrowserUrl = group === 'primary' && isActive;
        return (
          <Activity key={tab.id} mode={isActive ? 'visible' : 'hidden'}>
            <TabSurface
              tab={tab}
              path={ownsBrowserUrl ? urlPath : tab.path}
              ownsBrowserUrl={ownsBrowserUrl}
            />
          </Activity>
        );
      })}
    </>
  );
};
